import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserFiles, clearUserFiles, fetchGeneratedDocuments, deleteGeneratedDocument } from '../../slice/apiSlice';
import { BookOpen, File, Trash2Icon, Star, Image as ImageIcon, ChevronDown, ChevronRight, Globe, Search, X, Folder, Eye, Download } from 'lucide-react';
import { clearSources, fetchGeneratedPdf } from '../../slice/sourceSlice';
import { ReactComponent as FileIcon } from '../../assets/files.svg';
import { ReactComponent as TaskIcon } from '../../assets/tasks.svg';
import PDFViewer from './PDFViwer';
import AnalysisFilePreview, { previewKind } from '../atomic/AnalysisFilePreview';
import constants, { BASE_URL, BASE_WORDURL } from '../constants';
import { useThemeColor } from '../../hooks/useThemeColor';
import SQ from '../atomic/SQ';
import DeleteModal from '../atomic/DeleteModal';
import { fetchSQByTaskId, selectSQ, clearSQ, selectSQFetchLoading } from '../../slice/sqSlice';
import { fetchTaskByIdAsync, selectSelectedTask } from '../../slice/taskSlice';
import { getTaskStatusesByHistoryAsync } from '../../slice/taskStatusSlice';
import { uploadFilesWithHistory, createChatHistory } from '../../Api/req';
import { useTranslation } from '../../i18n/LanguageContext';
import { toast } from 'react-hot-toast';
import { fetchPDF } from '../../slice/pdfViewslice';
import { addSource } from '../../slice/sourceSlice';
import { useLocation } from 'react-router-dom';
import Button from '../atomic/Button';
import * as pdfjsLib from 'pdfjs-dist';
import { authHeader } from '../../utils/authHeaders';
import { downloadAuthenticatedFile } from '../../utils/downloadFile';

// Set the worker source for pdfjs-dist
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

function flashTitleElem(sanitizedTitle, attempts = 0) {
  const titleElem = document.getElementById(`chat-title-${sanitizedTitle}`);
  if (titleElem) {
    titleElem.classList.add('flash-highlight');
    setTimeout(() => {
      titleElem.classList.remove('flash-highlight');
    }, 1000);
  } else if (attempts < 10) {
    setTimeout(() => flashTitleElem(sanitizedTitle, attempts + 1), 100);
  }
}

function sanitizeTitleForId(title) {
  return title
    ? title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
    : '';
}

function flashTitleElemWhenVisible(sanitizedTitle) {
  const titleId = `chat-title-${sanitizedTitle}`;
  let attempts = 0;
  const maxAttempts = 20;

  function tryFlash() {
    const titleElem = document.getElementById(titleId);
    if (titleElem) {
      const observer = new window.IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              titleElem.classList.add('flash-highlight');
              setTimeout(() => {
                titleElem.classList.remove('flash-highlight');
              }, 1000);
              obs.disconnect();
            }
          });
        },
        { threshold: 0.1 }
      );
      observer.observe(titleElem);
    } else if (attempts < maxAttempts) {
      attempts++;
      setTimeout(tryFlash, 100);
    }
  }
  tryFlash();
}

const ACCEPTED_FILE_TYPES = {
  'application/pdf': '.pdf',
  'application/msword': '.doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'application/vnd.ms-excel': '.xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'text/csv': '.csv',
  'application/csv': '.csv',
  'application/vnd.ms-powerpoint': '.ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
  'text/plain': '.txt',
  // Both .jpg and .jpeg report MIME type "image/jpeg" — since this map's
  // keys aren't otherwise read anywhere (only Object.values() is, for the
  // extension allowlist/accept attribute below), a second, non-standard key
  // is used here just so BOTH extensions land in that values list; a file
  // named "photo.jpeg" would otherwise fail validateFile's extension check
  // since only ".jpg" would be present. Mirrors ChatInput.jsx's identical
  // ACCEPTED_FILE_TYPES map — this Sidebar Files-panel upload is a separate
  // entry point to the same underlying upload feature.
  'image/jpeg': '.jpg',
  'image/jpg': '.jpeg',
  'image/png': '.png',
};

// Document Intelligence analyzes an image directly (no page-splitting to
// count, unlike a PDF) — each image upload is always exactly 1 page for OCR
// quota purposes. Capped at 3 per batch (separate from the overall
// max-files-per-analysis limit) since each is its own individual OCR call,
// not pages of one document.
const MAX_IMAGES_PER_UPLOAD = 3;
const isImageFile = (file) => file?.type?.startsWith('image/');

// Already-uploaded files only carry a `filename` (no MIME type), so image
// detection for the file list icon and the preview pane goes by extension.
const isImageFilename = (filename) => /\.(jpe?g|png)$/i.test(filename || '');

const Sidebar = ({ isOpen, onClose, historyId, taskID, taskName, onDescriptionSelect, onWidthChange, mode, setHistoryId, readOnly = false, ownerUserId = null, questionsEnabled = true, filesEnabled = true, allowLocalFiles = true, questionsSendDisabled = false, generatedFilesOnly = false }) => {
  const { organizations } = useSelector((state) => state.organizations);
  const { user } = useSelector((state) => state.user);
  const { data: userData } = useSelector((state) => state.admin);

  useEffect(() => {
    localStorage.setItem('pdfOpenedFromTab', 'files');
    localStorage.setItem('currentActiveTab', 'files');
    if (activeTab === 'files') {
      setActiveQuestionId(null);
      localStorage.removeItem('activeQuestionId');
    }

    const savedWidth = 445;
    const width = parseInt(savedWidth, 10);
    setSidebarWidth(width);

    const sidebarContainer = document.querySelector('.sidebar-container');
    if (sidebarContainer) {
      sidebarContainer.style.width = `${width}px`;
    }

    if (onWidthChange) {
      onWidthChange(width);
    }
  }, []);

  const [activeTab, setActiveTab] = useState('files');
  const [sidebarWidth, setSidebarWidth] = useState(0);
  const lastFetchedTaskRef = useRef(null); // prevents duplicate SQ fetches for same taskID
  const [pdfZoomLevel, setPdfZoomLevel] = useState(1.0);
  const [isDragging, setIsDragging] = useState(false);
  const sidebarRef = useRef(null);
  const resizeHandleRef = useRef(null);
  const [activeQuestionId, setActiveQuestionId] = useState(() => {
    const savedTab = localStorage.getItem('pdfOpenedFromTab');
    const savedQuestionId = localStorage.getItem('activeQuestionId');
    const pdfOpenTimestamp = localStorage.getItem('pdfOpenTimestamp');
    const comingFromPdfView = pdfOpenTimestamp && savedQuestionId;
    return savedTab === 'tasks' && comingFromPdfView ? savedQuestionId : null;
  });
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [imagePreviewError, setImagePreviewError] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [pendingDeleteFile, setPendingDeleteFile] = useState(null);
  // filesSubTab is pure user intent (which sub-tab they last clicked) — it is
  // never force-written by an effect. What's actually shown is the derived
  // effectiveFilesSubTab below, computed fresh every render: generatedFilesOnly
  // always wins regardless of what the user last clicked, with no separate
  // effect that has to "catch up" to that prop a render late.
  const [filesSubTab, setFilesSubTab] = useState('uploaded');
  const effectiveFilesSubTab = generatedFilesOnly ? 'generated' : filesSubTab;
  const [pendingDeleteGeneratedDoc, setPendingDeleteGeneratedDoc] = useState(null);
  const [deletingGeneratedDocId, setDeletingGeneratedDocId] = useState(null);
  const [hasPendingFiles, setHasPendingFiles] = useState(true);
  const [files, setFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPdfFullscreen, setIsPdfFullscreen] = useState(false);
  const fileInputRef = useRef(null);
  const questionCatalogRef = useRef(null);

  const state = useSelector((state) => state.history);
  const { chatHistory = [], status, error } = useSelector((state) => state.history);
  const hname = chatHistory?.history_records;
  const dispatch = useDispatch();
  const { userFiles, userFilesStatus, userFilesError, userFilesHistoryId, generatedDocuments, generatedDocumentsStatus } = useSelector((state) => state.api);
  const questions = useSelector(selectSQ);
  const sqFetchLoading = useSelector(selectSQFetchLoading);
  const currentSQTaskId = useSelector((state) => state.sq.currentTaskId);
  // true only when data is confirmed ready for THIS task — prevents re-spinner on tab switch
  const sqReady = !sqFetchLoading && currentSQTaskId?.toString() === taskID?.toString();
  const taskStatuses = useSelector((state) => state.taskStatus.taskStatuses);
  const { aispaceColor } = useThemeColor();
  const { t } = useTranslation();
  const { currentPdf, loading, error: sourceError, errorCode: sourceErrorCode } = useSelector((state) => state.source);
  const location = useLocation();

  // Fetch SQ when taskID changes — parallel with layout fetch
  // clearSQ() fires SYNCHRONOUSLY first so spinner shows immediately (no old-count flash)
  // Ref guard prevents double-fetch when mode or other deps change without taskID changing
  useEffect(() => {
    if (!taskID || mode === 'chat') return;
    if (lastFetchedTaskRef.current === taskID) return; // already fetched for this task
    lastFetchedTaskRef.current = taskID;
    dispatch(clearSQ());           // immediate sync clear → spinner shows before async pending
    dispatch(fetchSQByTaskId(taskID));
  }, [taskID, dispatch, mode]);

  // When taskID clears, reset the ref so next project triggers a fresh fetch
  useEffect(() => {
    if (!taskID) lastFetchedTaskRef.current = null;
  }, [taskID]);

  // Tab management based on questionsEnabled — separate from fetch to avoid gating
  useEffect(() => {
    if (mode === 'chat' || !questionsEnabled) {
      setActiveTab('files');
      setActiveQuestionId(null);
      localStorage.removeItem('activeQuestionId');
      localStorage.setItem('pdfOpenedFromTab', 'files');
      localStorage.setItem('currentActiveTab', 'files');
      if (questionsEnabled === false) {
        dispatch(clearSQ());
        lastFetchedTaskRef.current = null; // allow re-fetch if re-enabled
      }
    }
  }, [taskID, dispatch, mode, questionsEnabled]);

  // Auto-correct activeTab when tabs become disabled
  useEffect(() => {
    if (!filesEnabled && !questionsEnabled) {
      // Both disabled — keep activeTab but content is guarded; nothing to switch to
      return;
    }
    if (activeTab === 'files' && !filesEnabled && questionsEnabled) {
      setActiveTab('tasks');
    } else if (activeTab === 'tasks' && !questionsEnabled && filesEnabled) {
      setActiveTab('files');
    }
  }, [filesEnabled, questionsEnabled, activeTab]);

  useEffect(() => {
    const pdfOpenTimestamp = localStorage.getItem('pdfOpenTimestamp');
    const initialPageLoad = !pdfOpenTimestamp;
    if (activeTab === 'files' || (initialPageLoad && activeTab === 'tasks')) {
      setActiveQuestionId(null);
      localStorage.removeItem('activeQuestionId');
    }
    if (activeQuestionId && activeTab === 'tasks') {
      scrollToActiveQuestion();
    }
  }, []);

  useEffect(() => {
    if (activeQuestionId && activeTab === 'tasks' && questionCatalogRef.current) {
      scrollToActiveQuestion();
    }
  }, [activeQuestionId, activeTab]);

  useEffect(() => {
    const handlePageLoad = () => {
      localStorage.removeItem('activeQuestionId');
      localStorage.removeItem('pdfSourceMessageId');
      localStorage.removeItem('pdfSourceMessageTitle');
      localStorage.removeItem('pdfOpenTimestamp');
      localStorage.removeItem('pdfOpenedFromTab');
      localStorage.removeItem('currentActiveTab');
      setActiveQuestionId(null);
    };

    handlePageLoad();
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        handlePageLoad();
      }
    });

    return () => {
      window.removeEventListener('pageshow', handlePageLoad);
    };
  }, []);

  useEffect(() => {
    if (historyId) {
      dispatch(getTaskStatusesByHistoryAsync(historyId));
    }
  }, [historyId, dispatch]);

  const userId = (readOnly && ownerUserId) ? String(ownerUserId) : localStorage.getItem('id');

  useEffect(() => {
    if (currentPdf.blob) {
      setImagePreviewError(false);
      const blobUrl = URL.createObjectURL(currentPdf.blob);
      setPdfBlobUrl(blobUrl);
      return () => URL.revokeObjectURL(blobUrl);
    }
    // No blob (a new source failed to load, or none selected yet) — clear
    // any previous URL so a failed fetch can't fall through to rendering
    // the PREVIOUS citation's stale PDF/image.
    setPdfBlobUrl(null);
  }, [currentPdf.blob]);

  // A citation was clicked (filename set) but the fetch ended in an error
  // and there's no blob/URL to show — surface this explicitly instead of
  // silently falling back to the tasks/questions tab or rendering a blank
  // PDFViewer with file=null (the previous "No PDF file provided" bug).
  const sourceLoadFailed = !!currentPdf.filename && !!sourceError && !loading && !pdfBlobUrl;

  const handleFileClick = () => {
    if (!allowLocalFiles) return;
    fileInputRef.current?.click();
  };

  const validateFile = (file) => {
    if (!file) return { isValid: false, error: t('sidebar.noFileSelected') };
    const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;
    const isValidType = Object.values(ACCEPTED_FILE_TYPES).includes(fileExtension);
    if (!isValidType) {
      return {
        isValid: false,
        error: t('sidebar.invalidFileType', { types: Object.values(ACCEPTED_FILE_TYPES).join(', ') }),
      };
    }
    return { isValid: true, error: null };
  };

  const handleFileChange = async (event) => {
    const selectedFiles = Array.from(event.target.files);
    const validFiles = [];
    let acceptedImageCount = 0;

    for (const file of selectedFiles) {
      const { isValid, error } = validateFile(file);
      if (isValid) {
        if (file.type === 'application/pdf') {
          try {
            const arrayBuffer = await file.arrayBuffer();
            const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
            file._pageCount = pdf.numPages; // Attach page count to file object
            validFiles.push(file);
            await pdf.destroy();
          } catch (err) {
            toast.error(t('sidebar.pdfProcessingError', { filename: file.name }));
          }
        } else if (file.type.includes('word') || file.type.includes('excel') || file.type.includes('spreadsheet') || file.type === 'text/csv' || file.type === 'application/csv' || /\.(xls|xlsx|csv)$/i.test(file.name)) {
            // Word + Excel + CSV all go through the same /convert-and-count
            // endpoint; the backend converts each format to PDF before storage.
            setIsConverting(true);
            const formData = new FormData();
            formData.append('file', file);
            try {
                const response = await fetch(`${BASE_URL}/convert-and-count`, {
                    method: 'POST',
                    headers: { ...authHeader() },
                    body: formData,
                });
                const data = await response.json();
                if (response.ok) {
                    file._pageCount = data.page_count;
                } else {
                    toast.error(data.error || t('sidebar.convertFileError'));
                    continue;
                }
            } catch (e) {
                toast.error(t('sidebar.convertFileError'));
                continue;
            } finally {
                setIsConverting(false);
            }
            validFiles.push(file);
        } else if (isImageFile(file)) {
          // Document Intelligence analyzes an image directly — no pages to
          // split/count like a PDF, so this is always exactly 1 for OCR
          // quota purposes. Capped at MAX_IMAGES_PER_UPLOAD per batch.
          if (acceptedImageCount >= MAX_IMAGES_PER_UPLOAD) {
            toast.error(t('chatActions.tooManyImages', { max: MAX_IMAGES_PER_UPLOAD }));
            continue;
          }
          file._pageCount = 1;
          acceptedImageCount += 1;
          validFiles.push(file);
        } else {
          file._pageCount = 0; // Non-PDF files have 0 pages
          validFiles.push(file);
        }
      } else {
        toast.error(t('sidebar.fileValidationError', { filename: file.name, error }));
      }
    }

    const isAnalysisRoute = location.pathname.startsWith('/analysis') || location.pathname === '/';
    const currentFileCount = files.length;
    const uploadedFileCount = (userFiles?.files?.filter(f => !f.is_default)?.length) || 0;
    const maxFiles = isAnalysisRoute ? 30 : 10;
    const totalPages = validFiles.reduce((acc, file) => acc + (file._pageCount || 0), 0);
    console.log('[Sidebar][handleFileChange] totalPages:', totalPages, {
      selectedFiles: selectedFiles.map(f => ({ name: f.name, type: f.type })),
      validFiles: validFiles.map(f => ({ name: f.name, pages: f._pageCount })),
      historyId,
      taskID,
    });

    // Reset file input to allow re-selecting same files
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (validFiles.length === 0) {
      return;
    }

    if (isAnalysisRoute && uploadedFileCount + currentFileCount + validFiles.length > maxFiles) {
      const allowedFiles = validFiles.slice(0, maxFiles - uploadedFileCount - currentFileCount);
      const rejectedCount = validFiles.length - allowedFiles.length;
      if (rejectedCount > 0) {
        toast.error(
          t('sidebar.maxFilesReached', { max: maxFiles, uploaded: uploadedFileCount, rejected: rejectedCount })
        );
      }
      if (allowedFiles.length > 0) {
        // Store files in state for user review before upload (like docai-demo-dev)
        setFiles(prev => [...prev, ...allowedFiles]);
      }
    } else {
      // Store files in state for user review before upload (like docai-demo-dev)
      setFiles(prev => [...prev, ...validFiles]);
    }
  };

  // Helper function to directly upload files
  const uploadFilesDirectly = async (filesToUpload) => {
    if (filesToUpload.length === 0) return;

    const organizationId = userData?.organization_id || null;
    if (!organizationId) {
      toast.error(t('sidebar.noOrganization'));
      return;
    }

    setIsProcessing(true);
    setHasPendingFiles(true);

    try {
      let currentHistoryId = historyId;

      if (!currentHistoryId) {
        const historyResult = await createChatHistory(userId, '40');
        currentHistoryId = historyResult.history_id || historyResult.id;
        if (setHistoryId) setHistoryId(currentHistoryId);
      }

      // Page counting and the per-session upload limit are enforced
      // server-side in /api/uploader itself now — it recomputes real page
      // counts from the file bytes and rejects the whole batch if it
      // doesn't fit, rather than trusting a client-side pre-check.
      const page_counts = filesToUpload.map(f => f._pageCount || 0);
      const uploadResult = await uploadFilesWithHistory(filesToUpload, userId, taskID || '40', currentHistoryId, page_counts);
      console.log('Upload success:', uploadResult);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      dispatch(
        fetchUserFiles({
          userId: userId,
          historyId: currentHistoryId || historyId,
          taskId: getEffectiveTaskId(),
          organizationId: userData?.organization_id,
        })
      );

      toast.success(t('sidebar.uploadSuccess'));
    } catch (error) {
      console.error('Error uploading files:', error);
      if (error?.code === 'history_limit_reached') {
        toast.error(t('homepage.historyLimitReached', { limit: error.limit }));
      } else if (error?.code === 'upload_page_limit_exceeded') {
        toast.error(t('chatActions.uploadPageLimitExceeded', { limit: error.limit, attempted: error.attempted }));
      } else {
        toast.error(error?.message || t('sidebar.uploadFilesError'));
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemoveFile = (index) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error(t('sidebar.selectFilesToUpload'));
      return;
    }

    const organizationId = userData?.organization_id || null;
    if (!organizationId) {
      toast.error(t('sidebar.noOrganization'));
      setIsProcessing(false);
      return;
    }

    const isAnalysisRoute = location.pathname.startsWith('/analysis') || location.pathname === '/';
    if (isAnalysisRoute) {
      const totalFileCount = ((userFiles?.files?.filter(f => !f.is_default)?.length) || 0) + files.length;
      if (totalFileCount > 30) {
        toast.error(
          t('sidebar.maxFilesReachedShort', { max: 30, uploaded: (userFiles?.files?.filter(f => !f.is_default)?.length) || 0 })
        );
        setIsProcessing(false);
        return;
      }
    }

    setIsProcessing(true);
    setHasPendingFiles(true);

    try {
      let currentHistoryId = historyId;

      if (!currentHistoryId) {
        const historyResult = await createChatHistory(userId, '40');
        currentHistoryId = historyResult.history_id || historyResult.id;
        if (setHistoryId) setHistoryId(currentHistoryId);
      }

      // Page counting and the per-session upload limit are enforced
      // server-side in /api/uploader itself now — see uploadFilesDirectly above.
      const page_counts = files.map(f => f._pageCount || 0);
      const uploadResult = await uploadFilesWithHistory(files, userId, taskID || '40', currentHistoryId, page_counts);
      console.log('Upload success:', uploadResult);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setFiles([]);

      dispatch(
        fetchUserFiles({
          userId: userId,
          historyId: currentHistoryId || historyId,
          taskId: getEffectiveTaskId(),
          organizationId: userData?.organization_id,
        })
      );

      toast.success(t('sidebar.refreshSuccess'));
    } catch (error) {
      console.error('Error uploading files:', error);
      if (error?.code === 'history_limit_reached') {
        toast.error(t('homepage.historyLimitReached', { limit: error.limit }));
      } else if (error?.code === 'upload_page_limit_exceeded') {
        toast.error(t('chatActions.uploadPageLimitExceeded', { limit: error.limit, attempted: error.attempted }));
      } else {
        toast.error(error?.message || t('sidebar.uploadFilesError'));
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Helper to get the effective task ID
  const getEffectiveTaskId = () => mode === 'chat' ? '40' : taskID;

  const handleRefreshFiles = async () => {
    if (!userId || !historyId) {
      toast.error(t('sidebar.cannotRefresh'));
      return;
    }

    setIsRefreshing(true);
    try {
      dispatch(clearUserFiles());
      dispatch(fetchUserFiles({ userId, historyId, taskId: getEffectiveTaskId(), organizationId: userData?.organization_id }));
      toast.success(t('sidebar.refreshSuccess'));
    } catch (error) {
      console.error('Error refreshing files:', error);
      toast.error(t('sidebar.refreshFilesError'));
    } finally {
      setIsRefreshing(false);
    }
  };

  // Track previous historyId and taskID to detect changes and prevent duplicate fetches
  const prevHistoryIdRef = useRef(null);
  const prevTaskIdRef = useRef(null);
  const isFetchingRef = useRef(false);
  const [isWaitingForTaskId, setIsWaitingForTaskId] = useState(false);

  useEffect(() => {
    // Get effective task ID
    const effectiveTaskId = getEffectiveTaskId();

    // Check if historyId changed
    const historyIdChanged = prevHistoryIdRef.current !== historyId;

    // Clear files and questions when historyId changes (switching projects)
    if (historyIdChanged && historyId) {
      dispatch(clearUserFiles());
      // NOT clearing generatedDocuments — that briefly zeroes the count Web-only mode's sidebar visibility depends on, flashing it hidden on every switch.
      dispatch(clearSQ());
      setFilesSubTab('uploaded'); // don't carry over "Generated" selection from the previous conversation
      prevHistoryIdRef.current = historyId;
      prevTaskIdRef.current = null; // Reset taskId ref when historyId changes
    }

    // If we have historyId but no taskID yet, we're waiting
    if (historyId && !effectiveTaskId) {
      setIsWaitingForTaskId(true);
      return;
    }

    setIsWaitingForTaskId(false);

    // Only fetch if taskID changed (historyId change already handled above)
    const shouldFetch =
      userId &&
      historyId &&
      effectiveTaskId &&
      prevTaskIdRef.current !== effectiveTaskId;

    if (shouldFetch) {
      prevTaskIdRef.current = effectiveTaskId;
      isFetchingRef.current = true;
      dispatch(fetchUserFiles({ userId, historyId, taskId: effectiveTaskId, organizationId: userData?.organization_id }))
        .finally(() => {
          isFetchingRef.current = false;
        });
    }
  }, [userId, historyId, taskID, userData?.organization_id, dispatch]);

  // Track previous activeTab to detect tab switches only
  const prevActiveTabRef = useRef(activeTab);

  useEffect(() => {
    // Only refetch on actual tab switch to 'files', not on historyId change
    const isTabSwitch = prevActiveTabRef.current !== activeTab;
    prevActiveTabRef.current = activeTab;

    const effectiveTaskId = getEffectiveTaskId();

    if (isTabSwitch && activeTab === 'files' && userId && historyId && effectiveTaskId && !isFetchingRef.current) {
      if (!userFiles?.files?.length && userFilesStatus !== 'loading') {
        console.log('Refetching files due to tab switch');
        dispatch(fetchUserFiles({ userId, historyId, taskId: effectiveTaskId, organizationId: userData?.organization_id }));
      }
    }
  }, [activeTab, userId, historyId, dispatch, userFilesStatus, userFiles?.files?.length, taskID]);

  // Generated Files tab — fetched whenever the Files panel is active and we
  // have a real history/user (independent of which sub-tab is selected, so
  // the tab can be hidden/shown by count without the user ever clicking
  // into it). Same (historyId, userId) scope as Uploaded files — this
  // conversation's own generated documents only, see fetchUserFiles above.
  useEffect(() => {
    if (activeTab === 'files' && userId && historyId) {
      dispatch(fetchGeneratedDocuments({ userId, historyId }));
    }
  }, [activeTab, userId, historyId, dispatch, generatedFilesOnly]);

  // If the currently-selected sub-tab disappears (switched to a
  // conversation with no generated files while "Generated files" was
  // active), fall back to Uploaded rather than showing an empty/vanished tab.
  useEffect(() => {
    if (filesSubTab === 'generated' && generatedDocumentsStatus === 'succeeded' && generatedDocuments.length === 0) {
      setFilesSubTab('uploaded');
    }
  }, [filesSubTab, generatedDocumentsStatus, generatedDocuments.length]);

  const scrollToActiveQuestion = () => {
    setTimeout(() => {
      if (questionCatalogRef.current) {
        const activeQuestionElement = questionCatalogRef.current.querySelector('[data-active="true"]');
        if (activeQuestionElement) {
          console.log('Found active question, scrolling to:', activeQuestionElement.id);
          activeQuestionElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          const container = document.querySelector('.overflow-y-auto');
          if (container) {
            const elementTop = activeQuestionElement.getBoundingClientRect().top;
            const containerTop = container.getBoundingClientRect().top;
            const offsetPosition = elementTop - containerTop - 100;
            container.scrollBy({
              top: offsetPosition,
              behavior: 'smooth',
            });
          }
        } else {
          console.log('No active question element found to scroll to');
        }
      }
    }, 500);
  };

  const handleClose = () => {
    if (!isPdfFullscreen && !pdfBlobUrl && !currentPdf.blob) {
      // Do nothing regarding sidebar width
    }

    if (isPdfFullscreen) {
      setIsPdfFullscreen(false);
    } else if (pdfBlobUrl || currentPdf.blob) {
      if (mode === 'chat') {
        dispatch(clearSources());
        setPdfBlobUrl(null);
        setActiveTab('files');
        setActiveQuestionId(null);
        localStorage.removeItem('activeQuestionId');
        localStorage.setItem('pdfOpenedFromTab', 'files');
        localStorage.setItem('currentActiveTab', 'files');
      } else {
        const savedTab = localStorage.getItem('pdfOpenedFromTab');
        const sourceMessageId = localStorage.getItem('pdfSourceMessageId');
        const sourceMessageTitle = localStorage.getItem('pdfSourceMessageTitle');

        console.log('Closing PDF, source message title:', sourceMessageTitle);

        if (sourceMessageTitle && questions && questions.length > 0) {
          const matchingQuestion = questions.find(
            (q) => q.title && q.title.toLowerCase() === sourceMessageTitle.toLowerCase()
          );
          if (matchingQuestion) {
            console.log('Found matching question!', matchingQuestion);
            setActiveQuestionId(matchingQuestion.id);
            localStorage.setItem('activeQuestionId', matchingQuestion.id);
          } else {
            console.log('No matching question found by title');
            setActiveQuestionId(sourceMessageId);
          }
        } else if (sourceMessageId) {
          setActiveQuestionId(sourceMessageId);
        }

        dispatch(clearSources());
        setPdfBlobUrl(null);

        if (savedTab) {
          setActiveTab(savedTab);
          console.log('Restoring tab to:', savedTab);
          if (savedTab === 'tasks') {
            scrollToActiveQuestion();
          }
        }
      }

      if (userId && historyId) {
        dispatch(fetchUserFiles({ userId, historyId, taskId: getEffectiveTaskId(), organizationId: userData?.organization_id }));
      }
    } else {
      onClose();
    }
  };

  const handleTabChange = (tab) => {
    const previousTab = activeTab;
    setActiveTab(tab);
    localStorage.setItem('pdfOpenedFromTab', tab);
    localStorage.setItem('currentActiveTab', tab);

    if (tab === 'files' || (previousTab === 'files' && tab === 'tasks')) {
      setActiveQuestionId(null);
      localStorage.removeItem('activeQuestionId');
      console.log('Clearing active question when switching tabs');
    } else if (tab === 'tasks' && previousTab === 'files') {
      if (activeQuestionId) {
        scrollToActiveQuestion();
      }
    }

    if (tab === 'files') {
      dispatch(fetchUserFiles({ userId, historyId, taskId: getEffectiveTaskId(), organizationId: userData?.organization_id }));
    }
    if (tab === 'tasks' && taskID && mode !== 'chat') {
      dispatch(fetchSQByTaskId(taskID));
    }
  };

  const handleDeleteFile = async (fileId, filename) => {
    try {
      setDeletingFileId(fileId);
      const response = await fetch(
        `${BASE_URL}/files/by-id/${fileId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            ...authHeader(),
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete file');
      }

      dispatch(fetchUserFiles({ userId, historyId, taskId: getEffectiveTaskId(), organizationId: userData?.organization_id }));
    } catch (error) {
      console.error('Error deleting file:', error);
    } finally {
      setDeletingFileId(null);
    }
  };

  const handleDeleteGeneratedDoc = async (documentId) => {
    try {
      setDeletingGeneratedDocId(documentId);
      await dispatch(deleteGeneratedDocument({ documentId, userId })).unwrap();
    } catch (error) {
      console.error('Error deleting generated document:', error);
      toast.error(t('sidebar.deleteGeneratedFileError'));
    } finally {
      setDeletingGeneratedDocId(null);
    }
  };

  const formatGeneratedDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const now = new Date();
    const isSameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    if (isSameDay(date, now)) return t('sidebar.today');
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (isSameDay(date, yesterday)) return t('sidebar.yesterday');
    return date.toLocaleDateString();
  };

  const handlePreviewGeneratedDoc = (doc) => {
    dispatch(fetchGeneratedPdf({ documentId: doc.id, userId, filename: doc.filename }));
  };

  const [previewAnalysisDoc, setPreviewAnalysisDoc] = useState(null);

  const handleDownloadAnalysisFile = (doc) => {
    downloadAuthenticatedFile(`${BASE_WORDURL}/download/${encodeURIComponent(doc.id)}`, doc.filename || doc.title)
      .catch(() => toast.error(t('dataAnalysis.downloadError')));
  };

  const handleDownloadGeneratedDoc = (doc, format) => {
    const name = format === 'pdf' ? (doc.filename || doc.title || 'document').replace(/\.docx$/, '.pdf') : (doc.filename || doc.title || 'document');
    downloadAuthenticatedFile(`${BASE_WORDURL}/download/${doc.id}?format=${format}`, name)
      .catch(() => toast.error(t('chatActions.wordDownloadError')));
  };

  // Separate user files and default files
  const userUploadedFiles = (userFiles?.files?.filter(f => !f.is_default) || [])
    .filter(f => allowLocalFiles || f.source_type?.toLowerCase() === 'sharepoint');
  const defaultFiles = userFiles?.files?.filter(f => f.is_default) || [];

  // Chat-mode only: SharePoint-origin default files get grouped into a
  // Knowledge Area > Site tree instead of the flat "Standard" list — every
  // other default file (local/manually-uploaded library files) stays in the
  // flat section unchanged.
  const sharePointDefaultFiles = defaultFiles.filter(f => f.sharepoint_site_id);
  const otherDefaultFiles = defaultFiles.filter(f => !f.sharepoint_site_id);

  const [fileSearch, setFileSearch] = useState('');
  // One Set tracks every level (Knowledge Area, Site, and each nested
  // folder/library) that the user has toggled away from its *default*
  // state — it does not mean "collapsed" uniformly. Knowledge Area and Site
  // default to expanded, so a toggle there means collapsed; everything
  // below a Site (library/folder) defaults to collapsed, so a toggle there
  // means expanded. isNodeCollapsed() below applies the right meaning per
  // level; toggleKey() itself just flips set membership either way.
  const [collapsedKeys, setCollapsedKeys] = useState(() => new Set());
  const toggleKey = (key) => {
    setCollapsedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const isNodeCollapsed = (key, defaultCollapsed = false) => {
    if (searchTerm) return false; // always auto-expand while searching
    const toggled = collapsedKeys.has(key);
    return defaultCollapsed ? !toggled : toggled;
  };

  const searchTerm = fileSearch.trim().toLowerCase();
  const matchesSearch = (file) => !searchTerm || (file.filename || '').toLowerCase().includes(searchTerm);

  // Recursively group a site's files into folders using their real
  // (possibly deeply nested) SharePoint path, e.g.
  // "/Documents/Anleitungen/file.pdf" — same convention as the backend's
  // own get_imported_files_at_path, just built client-side here since we
  // already have every file's full path in one response.
  const buildFolderTree = (files) => {
    const root = { folders: {}, files: [], fileCount: 0 };
    files.forEach((file) => {
      const parts = (file.sharepoint_file_path || file.filename || '')
        .split('/').filter(Boolean);
      const folderParts = parts.slice(0, -1);
      let node = root;
      folderParts.forEach((name) => {
        if (!node.folders[name]) node.folders[name] = { name, folders: {}, files: [], fileCount: 0 };
        node = node.folders[name];
      });
      node.files.push(file);
    });
    const countFiles = (node) => {
      node.fileCount = node.files.length + Object.values(node.folders).reduce((sum, f) => sum + countFiles(f), 0);
      return node.fileCount;
    };
    countFiles(root);
    return root;
  };

  // Build the Knowledge Area > Site tree from the flat SharePoint file list.
  // A site with multiple Knowledge Areas appears once under each area
  // (mirrors the same grouping rule used in the "+ Add site" wizard).
  const noKnowledgeAreaLabel = t('sidebar.noKnowledgeArea');
  const kaTree = {};
  sharePointDefaultFiles.filter(matchesSearch).forEach((file) => {
    const areas = (file.knowledge_area_names && file.knowledge_area_names.length > 0)
      ? file.knowledge_area_names
      : [noKnowledgeAreaLabel];
    areas.forEach((areaName) => {
      if (!kaTree[areaName]) kaTree[areaName] = {};
      const siteId = file.sharepoint_site_id;
      if (!kaTree[areaName][siteId]) {
        kaTree[areaName][siteId] = { siteName: file.sharepoint_site_name, files: [] };
      }
      kaTree[areaName][siteId].files.push(file);
    });
  });
  const kaGroupNames = Object.keys(kaTree).sort((a, b) => {
    if (a === noKnowledgeAreaLabel) return 1;
    if (b === noKnowledgeAreaLabel) return -1;
    return a.localeCompare(b);
  });

  // File item component matching Figma design
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const FileItem = ({ file, showDelete = false, isDefault = false, compact = false }) => {
    // Determine source: SharePoint or Standard (device/local)
    const isSharePoint = file.source_type === 'sharepoint' ||
      (typeof file.uploaded_by === 'string' && file.uploaded_by.toLowerCase().startsWith('sharepoint'));

    // Status: Pending while processing, Done only when fully indexed (status === 'done').
    // Default files are always Done.
    const isPending = !isDefault && file.status !== 'done';

    const badges = {
      pending: { key: 'pending', label: t('sidebar.statusPending'), bg: '#FEF3C7', text: '#D97706', border: '#FDE68A' },
      done: { key: 'done', label: t('sidebar.statusDone'), bg: '#D1FAE5', text: '#059669', border: '#A7F3D0' },
      standard: { key: 'standard', label: t('sidebar.standardBadge'), bg: `${aispaceColor}15`, text: aispaceColor, border: `${aispaceColor}30` },
      sharepoint: { key: 'sharepoint', label: t('sidebar.sharepointBadge'), bg: `${aispaceColor}1A`, text: aispaceColor, border: `${aispaceColor}40` },
      analysis: { key: 'analysis', label: t('sidebar.uploadedBadge'), bg: '#DBEAFE', text: '#2563EB', border: '#BFDBFE' },
    };
    // Status badge: Pending → Done
    const statusBadge = isPending ? badges.pending : badges.done;
    // Source badge: SharePoint > Standard (admin default) > Analyse (user uploaded)
    const sourceBadge = isSharePoint
      ? badges.sharepoint
      : (isDefault || file.is_default)
        ? badges.standard
        : badges.analysis;

    const handleOpen = () => {
      localStorage.setItem('pdfOpenedFromTab', 'files');
      localStorage.setItem('currentActiveTab', 'files');
      localStorage.removeItem('activeQuestionId');
      setActiveQuestionId(null);
      dispatch(addSource({ filename: file?.filename, page: 1 }));
      dispatch(
        fetchPDF({
          userId,
          taskId: defaultTaskId(taskID),
          historyId,
          filename: file?.filename,
          page: 1,
          itemId: file?.sharepoint_item_id,
        })
      );
    };

    // Compact row for the SharePoint folder tree — sized to match the
    // Folder/Site/Knowledge-Area rows around it (small icon, single-line
    // text, no badges) instead of the full card used in the flat
    // "Standard"/"You" sections below the tree.
    if (compact) {
      return (
        <button
          onClick={handleOpen}
          className="w-full flex items-center gap-2 py-1.5 hover:bg-[#F9FAFB] rounded-lg transition-colors text-left"
        >
          {isImageFilename(file.filename)
            ? <ImageIcon size={14} className="text-[#6B7280] flex-shrink-0" />
            : <File size={14} className="text-[#6B7280] flex-shrink-0" />}
          <span className="text-[13px] text-[#374151] flex-1 truncate">{file.filename}</span>
        </button>
      );
    }

    return (
      <div
        className="flex items-center gap-3 p-3 hover:bg-[#F9FAFB] rounded-lg cursor-pointer transition-colors"
        onClick={handleOpen}
      >
        {/* File icon — already-uploaded files only carry `filename` (no
            MIME type), so image detection here goes by extension. */}
        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${aispaceColor}15` }}>
          {isImageFilename(file.filename) ? (
            <ImageIcon size={20} color={aispaceColor} strokeWidth={2} />
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={aispaceColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
          )}
        </div>
        {/* File info + status */}
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-medium text-[#111827] truncate">{file.filename}</p>
          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
            {/* Status badge: Ausstehend or Done — only for user-uploaded files, not default/agent files */}
            {!isDefault && (
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium"
                style={{ backgroundColor: statusBadge.bg, color: statusBadge.text, border: `1px solid ${statusBadge.border}` }}
              >
                {isPending && (
                  <span className="w-1.5 h-1.5 rounded-full mr-1 animate-pulse" style={{ backgroundColor: statusBadge.text }} />
                )}
                {isPending ? t('sidebar.statusPending') : t('sidebar.statusDone')}
              </span>
            )}
            {/* Source badge: Standard or SharePoint */}
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium"
              style={{ backgroundColor: sourceBadge.bg, color: sourceBadge.text, border: `1px solid ${sourceBadge.border}` }}
            >
              {sourceBadge.key === 'sharepoint'
                ? t('sidebar.sharepointBadge')
                : sourceBadge.key === 'analysis'
                  ? t('sidebar.uploadedBadge')
                  : t('sidebar.standardBadge')}
            </span>
          </div>
        </div>
        {/* Delete button for user files */}
        {showDelete && !readOnly && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setPendingDeleteFile({ id: file.id, filename: file.filename });
            }}
            className="p-1 text-[#9CA3AF] hover:text-[#EF4444] transition-colors"
            disabled={deletingFileId === file.id}
          >
            {deletingFileId === file.id ? (
              <div className="w-4 h-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
            ) : (
              <Trash2Icon className="h-4 w-4" />
            )}
          </button>
        )}
      </div>
    );
  }

  // Renders one folder level (and recurses into its sub-folders) inside the
  // chat sidebar's SharePoint tree. `keyPrefix` uniquely identifies this
  // folder's position in the tree (site id + path) so collapse state
  // survives independently per folder, at any depth. Collapsed by default
  // (isNodeCollapsed's defaultCollapsed=true) — only Knowledge Area and Site
  // levels default to expanded. `isLibrary` marks a Site's direct children,
  // i.e. the SharePoint document library itself rather than a folder inside
  // it, and renders with a distinct icon.
  const FolderNode = ({ node, keyPrefix, depth, isLibrary = false }) => {
    const isCollapsed = isNodeCollapsed(keyPrefix, true);
    return (
      <div style={{ marginLeft: depth > 0 ? 12 : 0 }}>
        <button
          onClick={() => toggleKey(keyPrefix)}
          className="w-full flex items-center gap-2 py-1.5 hover:bg-[#F9FAFB] rounded-lg transition-colors"
        >
          {isCollapsed ? <ChevronRight size={13} className="text-[#9CA3AF] flex-shrink-0" /> : <ChevronDown size={13} className="text-[#9CA3AF] flex-shrink-0" />}
          {isLibrary
            ? <BookOpen size={14} className="text-[#6B7280] flex-shrink-0" />
            : <Folder size={14} className="text-[#6B7280] flex-shrink-0" />}
          <span className="text-[13px] font-medium text-[#111827] flex-1 text-left truncate">{node.name}</span>
          <span className="text-[12px] text-[#9CA3AF]">{node.fileCount}</span>
        </button>
        {!isCollapsed && (
          <div className="ml-3">
            {Object.values(node.folders).map((child) => (
              <FolderNode key={child.name} node={child} keyPrefix={`${keyPrefix}/${child.name}`} depth={depth + 1} />
            ))}
            {node.files.map((file) => (
              <FileItem key={file.id} file={file} showDelete={false} isDefault={true} compact={true} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderFiles = () => {
    // Show loading if: files are loading, waiting for taskID, or files belong to a different historyId
    if (userFilesStatus === 'loading' || isWaitingForTaskId || (userFilesHistoryId && userFilesHistoryId !== historyId)) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: aispaceColor, borderTopColor: 'transparent' }}></div>
        </div>
      );
    }

    if (userFilesStatus === 'failed') {
      return <div className="p-3 text-red-500">{t('sidebar.filesLoadError', { error: userFilesError })}</div>;
    }

    // SharePoint default files render as a Knowledge Area > Site tree; every
    // other section (uploaded-in-chat, non-SharePoint standard files) stays
    // as a flat list. Applies to every real usage of this sidebar (the
    // per-agent conversation in Analysis.jsx, the fixed-task Chat.jsx, and
    // the read-only SharedView.jsx) — `mode` is not a reliable signal here,
    // it's only ever set to 'chat' by Chat.jsx and left undefined by the
    // other two, none of which need different behavior.
    {
      const searchedUserFiles = userUploadedFiles.filter(matchesSearch);
      const searchedOtherDefaults = otherDefaultFiles.filter(matchesSearch);
      const isEmpty = searchedUserFiles.length === 0 && searchedOtherDefaults.length === 0 && kaGroupNames.length === 0;

      return (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-6">
            {kaGroupNames.length > 0 && (
              <div>
                <h3 className="text-[12px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-2">{t('sidebar.sharepointSection')}</h3>
                <div className="flex flex-col gap-1">
                  {kaGroupNames.map((areaName) => {
                    const sites = Object.entries(kaTree[areaName]);
                    const areaFileCount = sites.reduce((sum, [, s]) => sum + s.files.length, 0);
                    const kaKey = `ka:${areaName}`;
                    // The whole SharePoint tree starts fully closed —
                    // Knowledge Area, Site, and every folder below default to
                    // collapsed — regardless of which page renders this
                    // sidebar (Chat.jsx, Analysis.jsx, SharedView.jsx).
                    // Nothing is open until the user clicks into it.
                    const isCollapsed = isNodeCollapsed(kaKey, true);
                    return (
                      <div key={areaName}>
                        <button
                          onClick={() => toggleKey(kaKey)}
                          className="w-full flex items-center gap-1.5 py-1.5 hover:bg-[#F9FAFB] rounded-lg transition-colors"
                        >
                          {isCollapsed ? <ChevronRight size={14} className="text-[#9CA3AF]" /> : <ChevronDown size={14} className="text-[#9CA3AF]" />}
                          <span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide flex-1 text-left truncate">{areaName}</span>
                          <span className="text-[11px] text-[#9CA3AF]">{areaFileCount}</span>
                        </button>
                        {!isCollapsed && sites.map(([siteId, site]) => {
                          const siteKey = `site:${siteId}`;
                          const siteCollapsed = isNodeCollapsed(siteKey, true);
                          const tree = buildFolderTree(site.files);
                          return (
                            <div key={siteId} className="ml-3">
                              <button
                                onClick={() => toggleKey(siteKey)}
                                className="w-full flex items-center gap-2 py-1.5 hover:bg-[#F9FAFB] rounded-lg transition-colors"
                              >
                                {siteCollapsed ? <ChevronRight size={13} className="text-[#9CA3AF]" /> : <ChevronDown size={13} className="text-[#9CA3AF]" />}
                                <Globe size={13} className="text-[#6B7280] flex-shrink-0" />
                                <span className="text-[13px] font-semibold text-[#111827] flex-1 text-left truncate">{site.siteName}</span>
                                <span className="text-[12px] text-[#9CA3AF]">{site.files.length}</span>
                              </button>
                              {!siteCollapsed && (
                                <div className="ml-3">
                                  {Object.values(tree.folders).map((folder) => (
                                    <FolderNode key={folder.name} node={folder} keyPrefix={`folder:${siteId}/${folder.name}`} depth={0} isLibrary={true} />
                                  ))}
                                  {tree.files.map((file) => (
                                    <FileItem key={file.id} file={file} showDelete={false} isDefault={true} compact={true} />
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Standard Section - non-SharePoint default/library files */}
            {searchedOtherDefaults.length > 0 && (
              <div>
                <h3 className="text-[14px] font-medium text-[#374151] mb-3">{t('sidebar.standardSection')}</h3>
                <div className="flex flex-col">
                  {searchedOtherDefaults.map((file) => (
                    <FileItem key={file.id} file={file} showDelete={false} isDefault={true} />
                  ))}
                </div>
              </div>
            )}

            {/* You Section - files uploaded in this chat */}
            {searchedUserFiles.length > 0 && (
              <div>
                <h3 className="text-[14px] font-medium text-[#374151] mb-3">{t('sidebar.youSection')}</h3>
                <div className="flex flex-col">
                  {searchedUserFiles.map((file) => (
                    <FileItem key={file.id} file={file} showDelete={true} />
                  ))}
                </div>
              </div>
            )}

            {isEmpty && (
              <div className="text-center py-8 text-[#9CA3AF]">
                {searchTerm ? t('sidebar.noFilesFound') : t('sidebar.noFilesUploaded')}
              </div>
            )}
          </div>
        </div>
      );
    }
  };

  const renderGeneratedFiles = () => {
    if (generatedDocumentsStatus === 'loading' && generatedDocuments.length === 0) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: aispaceColor, borderTopColor: 'transparent' }}></div>
        </div>
      );
    }

    if (generatedDocuments.length === 0) {
      return (
        <div className="text-center py-8 text-[#9CA3AF]">
          {t('sidebar.noGeneratedFiles')}
        </div>
      );
    }

    // Same card + header look as History.jsx's project list (the app's
    // reference "professional" table): a rounded bordered card, a plain
    // near-white header with just a bottom hairline (no boxed-in top
    // border), generous row padding, and the same row hover tint —
    // Users.jsx's bordered icon-button convention is kept for the actions
    // themselves.
    return (
      <div className="overflow-x-auto -mx-4 px-4">
        <div className="border border-[#E5E7EB] rounded-xl overflow-hidden">
        <table className="w-full text-left border-collapse m-0">
          <thead>
            <tr className="bg-[#F5F5F5] border-b border-[#E5E7EB]">
              <th className="pl-4 py-3.5 pr-2 text-[13px] font-medium text-[#6B7280] whitespace-nowrap border-0 bg-transparent">{t('sidebar.tableName')}</th>
              <th className="py-3.5 pr-2 text-[13px] font-medium text-[#6B7280] whitespace-nowrap border-0 bg-transparent">{t('sidebar.tableCreated')}</th>
              <th className="py-3.5 pr-2 text-[13px] font-medium text-[#6B7280] whitespace-nowrap border-0 bg-transparent">{t('sidebar.tableSize')}</th>
              <th className="py-3.5 pr-4 text-[13px] font-medium text-[#6B7280] whitespace-nowrap border-0 bg-transparent">{t('sidebar.tableActions')}</th>
            </tr>
          </thead>
          <tbody>
            {generatedDocuments.map((doc, index) => (
              <tr
                key={doc.id}
                className={`hover:bg-[#FAFAFA] transition-colors ${index !== generatedDocuments.length - 1 ? 'border-b border-[#E5E7EB]' : ''}`}
              >
                <td className="pl-4 py-3.5 pr-2 text-[13px] font-medium text-[#111827] max-w-[130px] truncate border-0" title={doc.title || doc.filename}>
                  {doc.title || doc.filename}
                </td>
                <td className="py-3.5 pr-2 text-[13px] text-[#6B7280] whitespace-nowrap border-0">{formatGeneratedDate(doc.created_at)}</td>
                <td className="py-3.5 pr-2 text-[13px] text-[#6B7280] whitespace-nowrap border-0">{formatFileSize(doc.file_size)}</td>
                <td className="py-3.5 pr-4 border-0">
                  <div className="flex items-center gap-1.5">
                    {doc.file_kind === 'analysis' ? (
                    <>
                    {previewKind(doc.filename) && (
                    <button
                      type="button"
                      onClick={() => setPreviewAnalysisDoc(doc)}
                      title={t('dataAnalysis.preview')}
                      aria-label={t('dataAnalysis.preview')}
                      className="w-7 h-7 flex-shrink-0 flex items-center justify-center text-[#6B7280] hover:text-[#374151] border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
                    >
                      <Eye size={14} />
                    </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDownloadAnalysisFile(doc)}
                      title={t('dataAnalysis.download')}
                      aria-label={t('dataAnalysis.download')}
                      className="w-7 h-7 flex-shrink-0 flex items-center justify-center text-[#6B7280] hover:text-[#374151] border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
                    >
                      <Download size={14} />
                    </button>
                    </>
                    ) : (
                    <>
                    <button
                      type="button"
                      onClick={() => handlePreviewGeneratedDoc(doc)}
                      title={t('sidebar.previewAction')}
                      className="w-7 h-7 flex-shrink-0 flex items-center justify-center text-[#6B7280] hover:text-[#374151] border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDownloadGeneratedDoc(doc, 'docx')}
                      title={t('sidebar.downloadWordAction')}
                      className="w-7 h-7 flex-shrink-0 flex items-center justify-center text-[13px] font-semibold text-[#6B7280] hover:text-[#374151] border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
                    >
                      W
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDownloadGeneratedDoc(doc, 'pdf')}
                      title={t('sidebar.downloadPdfAction')}
                      className="w-7 h-7 flex-shrink-0 flex items-center justify-center text-[13px] font-semibold text-[#6B7280] hover:text-[#374151] border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
                    >
                      P
                    </button>
                    </>
                    )}
                    {/* Not in read-only shared view — a share-link viewer must
                        never be able to delete the conversation owner's
                        generated document (mirrors the Uploaded list's own
                        delete button, which is gated the same way). */}
                    {!readOnly && (
                    <button
                      type="button"
                      onClick={() => setPendingDeleteGeneratedDoc(doc)}
                      disabled={deletingGeneratedDocId === doc.id}
                      title={t('sidebar.deleteFileTitle')}
                      className="w-7 h-7 flex-shrink-0 flex items-center justify-center text-[#9CA3AF] hover:text-[#EF4444] border border-[#E5E7EB] rounded-lg hover:bg-[#FEF2F2] transition-colors disabled:opacity-50"
                    >
                      <X size={14} />
                    </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    );
  };

  useEffect(() => {
    let intervalId = null;

    const startPolling = () => {
      if (activeTab === 'files' && userId) {
        intervalId = setInterval(() => {
          const hasPending = userFiles?.files?.some((file) => file.status === 'Pending');
          if (hasPending) {
            dispatch(fetchUserFiles({ userId, historyId, taskId: getEffectiveTaskId(), organizationId: userData?.organization_id }));
          } else {
            if (intervalId) {
              clearInterval(intervalId);
              intervalId = null;
            }
          }
        }, 20000);
      }
    };

    const hasPendingFiles = userFiles?.files?.some((file) => file.status === 'Pending');
    if (hasPendingFiles && activeTab === 'files' && userId) {
      startPolling();
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [userFiles, activeTab, userId, dispatch]);

  const handleDescriptionSelect = (question) => {
    if (!question || !question.title) {
      console.warn('Invalid question object:', question);
      return;
    }
    const sanitizedTitle = sanitizeTitleForId(question.title);
    const chatMsgElem = document.getElementById(`chat-message-${sanitizedTitle}`);
    if (chatMsgElem) {
      chatMsgElem.scrollIntoView({ behavior: 'smooth', block: 'center' });
      flashTitleElemWhenVisible(sanitizedTitle);
      return;
    }
    if (onDescriptionSelect) onDescriptionSelect(question);
  };

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    document.body.style.cursor = 'ew-resize';
    document.body.classList.add('resize-active');
  }, []);

  const handleMouseMove = (e) => {
    const newWidth = window.innerWidth - e.clientX;
    const maxWidth = window.innerWidth * 0.4;
    const minWidth = 445;

    if (newWidth >= minWidth && newWidth <= maxWidth) {
      const roundedWidth = Math.round(newWidth);
      localStorage.setItem('sidebarWidth', roundedWidth);
      setSidebarWidth(roundedWidth);

      if (onWidthChange) {
        onWidthChange(roundedWidth);
      }

      const newZoomLevel = 1.2 - ((newWidth - minWidth) / (maxWidth - minWidth)) * 0.4;
      setPdfZoomLevel(newZoomLevel);

      if (pdfBlobUrl || currentPdf.blob) {
        window.dispatchEvent(new Event('resize'));
      }

      const sidebarContainer = document.querySelector('.h-full.transition-all.duration-300');
      if (sidebarContainer) {
        sidebarContainer.style.transition = 'width 0.05s ease';
        sidebarContainer.style.width = `${newWidth}px`;
      }
    }
  };

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    document.body.style.cursor = '';
    document.body.classList.remove('resize-active');
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const defaultTaskId = (taskId) => (!taskId || taskId === 'null') ? '40' : taskId;

  useEffect(() => {
    // Clear all project-related state when navigating to a different page
    dispatch(clearSources());
    dispatch(clearUserFiles());
    dispatch(clearSQ());
    setPdfBlobUrl(null);
    setIsPdfFullscreen(false);
    // Reset refs to ensure fresh data for new project
    prevHistoryIdRef.current = null;
    prevTaskIdRef.current = null;
  }, [location.pathname]);


  return (
    <div
      ref={sidebarRef}
      className={`h-full w-full bg-white flex flex-col relative ${
        isPdfFullscreen || !isOpen ? 'overflow-hidden' : ''
      }`}
    >
      {isOpen && !isPdfFullscreen && (
        <div
          ref={resizeHandleRef}
          className="absolute left-0 top-0 h-full cursor-ew-resize z-40 group flex items-center justify-center"
          onMouseDown={handleMouseDown}
        >
          <div className="w-1 h-fit bg-white transition-colors duration-200"></div>
          {isDragging && <div className="fixed inset-0 z-40" />}
        </div>
      )}
      <div className="border-b border-[#E5E7EB] min-h-[65px] flex items-center">
        <div className="px-4 flex-1 flex justify-between items-center">
          {loading || pdfBlobUrl || sourceLoadFailed ? (
            <div className="flex items-center gap-2 group">
              <h1 className="font-medium p-3 truncate max-w-[300px] transition-colors duration-200" title={currentPdf.filename}>
                {currentPdf.filename}
              </h1>
              <div className="absolute invisible group-hover:visible bg-gray-200 text-black text-sm rounded px-2 py-1 mt-12 whitespace-nowrap z-50 shadow-lg">
                {currentPdf.filename}
              </div>
              {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-custom-yellow border-t-transparent" />
              )}
            </div>
          ) : (
            /* Tab buttons — only render container when at least one tab is visible */
            (questionsEnabled || filesEnabled || generatedFilesOnly) ? (
            <div className="flex items-center gap-1 bg-[#F3F4F6] rounded-lg p-1 overflow-hidden">
              {mode !== 'chat' && questionsEnabled && (
                <button
                  onClick={() => handleTabChange('tasks')}
                  className={`px-4 py-1.5 rounded-md text-[13px] font-medium transition-colors ${
                    activeTab === 'tasks'
                      ? 'bg-white text-[#111827] shadow-sm'
                      : 'text-[#6B7280] hover:text-[#374151]'
                  }`}
                >
                  {t('sidebar.fragen')}
                  {currentSQTaskId?.toString() === taskID?.toString() && (questions?.length || 0) > 0 && (
                    <span className="text-[#9CA3AF] ml-1">{questions.length}</span>
                  )}
                </button>
              )}
              {(filesEnabled || generatedFilesOnly) && (
                <button
                  onClick={() => handleTabChange('files')}
                  className={`px-4 py-1.5 rounded-md text-[13px] font-medium transition-colors ${
                    activeTab === 'files'
                      ? 'bg-white text-[#111827] shadow-sm'
                      : 'text-[#6B7280] hover:text-[#374151]'
                  }`}
                >
                  {generatedFilesOnly
                    ? t('sidebar.generatedFilesTab', { count: generatedDocuments.length })
                    : <>{t('sidebar.dateien')} <span className="text-[#9CA3AF] ml-1">{userFiles?.files?.length > 0 ? userFiles.files.length : (userFiles?.total_files || 0)}</span></>}
                </button>
              )}
            </div>
            ) : null
          )}
          <div className="flex items-center gap-2 ml-auto">
            {!readOnly && (
              <button
                onClick={handleRefreshFiles}
                disabled={isRefreshing}
                className="text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={t('sidebar.refreshFiles')}
                title={t('sidebar.refreshFiles')}
              >
                <img
                  src={require('../../assets/refresh.png')}
                  alt={t('sidebar.refreshAlt')}
                  className={`w-7 h-6 object-contain ${isRefreshing ? 'animate-spin' : ''}`}
                />
              </button>
            )}
            <button onClick={handleClose} className="text-gray-500 hover:text-gray-700" aria-label={t('sidebar.closeSidebar')}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto shadow-md">
        {loading || pdfBlobUrl || sourceLoadFailed ? (
          <>
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center gap-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-custom-yellow border-t-transparent" />
                  <p className="text-gray-600">
                    {isImageFilename(currentPdf.filename) ? t('sidebar.imageLoading') : t('sidebar.pdfLoading')}
                  </p>
                </div>
              </div>
            ) : sourceLoadFailed ? (
              <div className="flex items-center justify-center h-full p-6 text-center">
                <p className="text-gray-500">
                  {sourceErrorCode === 'sharepoint_file_stale'
                    ? t('sidebar.sourceFileStale')
                    : sourceErrorCode === 'file_decryption_failed'
                    ? t('sidebar.sourceFileDecryptionFailed')
                    : sourceErrorCode === 'timeout'
                    ? t('sidebar.sourceFileTimeout')
                    : sourceErrorCode === 'load_failed'
                    ? t('sidebar.sourceFileLoadFailed')
                    : t('sidebar.sourceFileNotFound')}
                </p>
              </div>
            ) : isImageFilename(currentPdf.filename) ? (
              <div className="w-full h-full flex items-center justify-center overflow-auto bg-[#F3F4F6] p-4">
                <img
                  src={pdfBlobUrl}
                  alt={currentPdf.filename}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    setImagePreviewError(true);
                  }}
                />
                {imagePreviewError && (
                  <p className="text-gray-500">{t('sidebar.imageLoadError')}</p>
                )}
              </div>
            ) : (
              <div className="w-full h-full" style={{ width: `${sidebarWidth}px` }}>
                <PDFViewer
                  file={currentPdf.blob}
                  initialPage={currentPdf.page}
                  zoomLevel={pdfZoomLevel}
                  onFullscreenChange={(isFullscreen) => setIsPdfFullscreen(isFullscreen)}
                />
              </div>
            )}
          </>
        ) : (
          <div className="w-full p-4 flex flex-col gap-4">
            {/* Both tabs disabled — show nothing, avoid rendering file/question content */}
            {!questionsEnabled && !filesEnabled && null}
            {activeTab === 'tasks' && (
              <div className="flex flex-col gap-4 mt-4" ref={questionCatalogRef}>
                {/* Spinner: only when questions not yet ready for this task
                    Once sqReady = true (data confirmed for this taskID), never spinner again on tab switch */}
                {taskID && questionsEnabled && !sqReady ? (
                  <div className="flex flex-col items-center justify-center py-10 gap-3">
                    <div
                      className="w-8 h-8 rounded-full border-[3px] border-t-transparent animate-spin"
                      style={{ borderColor: `${aispaceColor}40`, borderTopColor: 'transparent' }}
                    />
                    <p className="text-[13px] text-[#9CA3AF]">{t('sidebar.loadingQuestions') || 'Fragen werden geladen…'}</p>
                  </div>
                ) : (
                <div className="flex flex-col gap-4">
                  {(() => {
                    const savedQuestionId = localStorage.getItem('activeQuestionId');
                    const sourceTitle = localStorage.getItem('pdfSourceMessageTitle');
                    let foundExactTitleMatch = false;
                    let foundIdMatch = false;

                    if (questions.length === 0 || activeTab === 'files') {
                      return [];
                    }

                    if (sourceTitle) {
                      console.log('Available questions to match against:');
                      questions.forEach((q) => {
                        console.log(`- "${q.title}" (ID: ${q.id})`);
                      });
                    }

                    return questions.map((question, index) => {
                      const exactTitleMatch =
                        sourceTitle &&
                        question.title &&
                        sourceTitle.toLowerCase().trim() === question.title.toLowerCase().trim();
                      const idMatch =
                        savedQuestionId &&
                        question.id &&
                        savedQuestionId.toString() === question.id.toString();
                      const partialTitleMatch =
                        !exactTitleMatch &&
                        sourceTitle &&
                        question.title &&
                        (sourceTitle.toLowerCase().includes(question.title.toLowerCase()) ||
                          question.title.toLowerCase().includes(sourceTitle.toLowerCase()));
                      const isActive =
                        exactTitleMatch || (idMatch && !foundExactTitleMatch) || (partialTitleMatch && !foundExactTitleMatch && !foundIdMatch);

                      if (exactTitleMatch) foundExactTitleMatch = true;
                      if (idMatch) foundIdMatch = true;

                      if (isActive) {
                        console.log(
                          'Highlighting question:',
                          question.title,
                          exactTitleMatch ? '(exact title match)' : idMatch ? '(id match)' : '(partial match)'
                        );
                      }

                      return (
                        <SQ
                          key={question.id}
                          question={question}
                          index={index}
                          onDescriptionSelect={handleDescriptionSelect}
                          historyId={historyId}
                          isActive={isActive}
                          readOnly={readOnly}
                          disabled={questionsSendDisabled}
                        />
                      );
                    });
                  })()}
                </div>
                )}
              </div>
            )}
            {activeTab === 'files' && (questionsEnabled || filesEnabled || generatedFilesOnly) && (
              <div className="flex flex-col files-content">
                {/* Sticky header: sub-tabs + (Uploaded-only) upload area/search
                    box stay pinned while the file list/tree scrolls beneath
                    them. Negative margin cancels the parent's p-4 so this bar
                    spans full-bleed and sticks flush to the actual scroll
                    boundary, with matching padding re-applied so the content
                    itself still looks inset. */}
                <div className="sticky -top-4 -mx-4 -mt-4 z-10 bg-white pt-4 px-4 pb-3 mb-1 border-[#F3F4F6] flex flex-col gap-3">
                  {/* Uploaded / Generated files sub-tabs — Generated files
                      only ever lists THIS conversation's own Word-generation
                      output (see get_generated_documents_for_history), same
                      scope as the Uploaded list above it. The tab itself only
                      appears once this conversation has actually generated
                      something — nothing to switch to otherwise. */}
                  {generatedDocuments.length > 0 && !generatedFilesOnly && (
                  <div className="flex items-center gap-5 -mt-1 mb-1 border-b border-[#F3F4F6]">
                    <button
                      type="button"
                      onClick={() => setFilesSubTab('uploaded')}
                      className="pb-2.5 text-[14px] font-medium transition-colors"
                      style={{
                        color: effectiveFilesSubTab === 'uploaded' ? aispaceColor : '#9CA3AF',
                        borderBottom: effectiveFilesSubTab === 'uploaded' ? `2px solid ${aispaceColor}` : '2px solid transparent',
                      }}
                    >
                      {t('sidebar.uploadedTab')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setFilesSubTab('generated')}
                      className="pb-2.5 text-[14px] font-medium transition-colors"
                      style={{
                        color: effectiveFilesSubTab === 'generated' ? aispaceColor : '#9CA3AF',
                        borderBottom: effectiveFilesSubTab === 'generated' ? `2px solid ${aispaceColor}` : '2px solid transparent',
                      }}
                    >
                      {t('sidebar.generatedFilesTab', { count: generatedDocuments.length })}
                    </button>
                  </div>
                  )}

                  {/* Upload file button — only when files tab enabled, local uploads allowed, no files selected, not readOnly */}
                  {effectiveFilesSubTab === 'uploaded' && !readOnly && filesEnabled && allowLocalFiles && files.length === 0 && (
                    <button
                      onClick={handleFileClick}
                      disabled={isProcessing || isConverting}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 text-white rounded-lg text-[14px] font-medium transition-colors hover:opacity-90 disabled:opacity-50"
                      style={{ backgroundColor: aispaceColor }}
                    >
                      {(isProcessing || isConverting) ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="17 8 12 3 7 8"/>
                          <line x1="12" y1="3" x2="12" y2="15"/>
                        </svg>
                      )}
                      {isConverting ? t('sidebar.convertingState') : isProcessing ? t('sidebar.uploadingState') : t('sidebar.uploadFile')}
                    </button>
                  )}
                  {effectiveFilesSubTab === 'uploaded' && (
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.csv,.ppt,.pptx,.jpg,.jpeg,.png"
                    multiple
                    style={{ display: 'none' }}
                  />
                  )}

                  {/* Max files info — only relevant when upload is allowed */}
                  {effectiveFilesSubTab === 'uploaded' && !readOnly && filesEnabled && allowLocalFiles && (
                    <div className="text-[12px] text-[#9CA3AF] text-center">
                      {t('sidebar.maxFilesInfo', { count: 30, used: (((userFiles?.files?.filter(f => !f.is_default)?.length) || 0) + files.length) })}
                    </div>
                  )}

                  {/* Selected files before upload */}
                  {effectiveFilesSubTab === 'uploaded' && files.length > 0 && (
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-[13px] font-medium text-[#374151]">{t('sidebar.selectedFiles')}</span>
                        <span className="text-[12px] text-[#9CA3AF]">
                          {(((userFiles?.files?.filter(f => !f.is_default)?.length) || 0) + files.length)}/30
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {files.map((file, index) => (
                          <div key={index} className="flex items-center gap-1.5 bg-[#F3F4F6] rounded-lg px-3 py-1.5">
                            <span className="text-[12px] text-[#111827] truncate max-w-[150px]">{file.name}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveFile(index)}
                              className="text-[#9CA3AF] hover:text-[#EF4444] transition-colors flex-shrink-0"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                      {files.some(f => f.type === 'application/pdf' || f.name?.endsWith('.pdf')) && (
                        <p className="text-[12px] text-[#9CA3AF] mt-2">
                          {t('sidebar.totalPages', { count: files.reduce((acc, f) => acc + (f._pageCount || 0), 0) })}
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={handleUpload}
                        disabled={isProcessing || isConverting}
                        className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 text-white rounded-lg text-[14px] font-medium transition-colors hover:opacity-90 disabled:opacity-50"
                        style={{ backgroundColor: aispaceColor }}
                      >
                        {isConverting ? (
                          <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> {t('sidebar.convertingState')}</>
                        ) : isProcessing ? (
                          <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> {t('sidebar.uploadingState')}</>
                        ) : (
                          t('sidebar.addFiles')
                        )}
                      </button>
                    </div>
                  )}

                  {/* Search box — pinned alongside the upload area */}
                  {effectiveFilesSubTab === 'uploaded' && (
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none" />
                    <input
                      type="text"
                      value={fileSearch}
                      onChange={(e) => setFileSearch(e.target.value)}
                      placeholder={t('sidebar.searchFilesPlaceholder')}
                      className="w-full pl-9 pr-8 py-2.5 text-[14px] border border-[#E5E7EB] rounded-xl focus:outline-none placeholder-[#9CA3AF] transition-colors"
                      onFocus={(e) => e.target.style.borderColor = aispaceColor}
                      onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                    />
                    {fileSearch && (
                      <button onClick={() => setFileSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 hover:bg-[#F3F4F6] rounded">
                        <X size={14} className="text-[#9CA3AF]" />
                      </button>
                    )}
                  </div>
                  )}
                </div>

                {/* File lists - You/Default sections, or Generated files table */}
                {effectiveFilesSubTab === 'generated' ? renderGeneratedFiles() : (
                  <div className="mt-2">{renderFiles()}</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <DeleteModal
        isOpen={!!pendingDeleteFile}
        onClose={() => setPendingDeleteFile(null)}
        onDelete={async () => {
          if (!pendingDeleteFile) return;
          await handleDeleteFile(pendingDeleteFile.id, pendingDeleteFile.filename);
          setPendingDeleteFile(null);
        }}
        title={t('sidebar.deleteFileTitle')}
        message={t('sidebar.deleteFileMessage', { filename: pendingDeleteFile?.filename || '' })}
      />

      <DeleteModal
        isOpen={!!pendingDeleteGeneratedDoc}
        onClose={() => setPendingDeleteGeneratedDoc(null)}
        onDelete={async () => {
          if (!pendingDeleteGeneratedDoc) return;
          await handleDeleteGeneratedDoc(pendingDeleteGeneratedDoc.id);
          setPendingDeleteGeneratedDoc(null);
        }}
        title={t('sidebar.deleteGeneratedFileTitle')}
        message={t('sidebar.deleteGeneratedFileMessage', { filename: pendingDeleteGeneratedDoc?.title || pendingDeleteGeneratedDoc?.filename || '' })}
      />
      {previewAnalysisDoc && (
        <AnalysisFilePreview doc={previewAnalysisDoc} t={t} onClose={() => setPreviewAnalysisDoc(null)} />
      )}
    </div>
  );
};

export default Sidebar;